import { signal } from '@preact/signals';
import { useMemo, useRef } from 'preact/hooks';
import { invalidate } from '@/stores/queryCache';

export interface UseMutationOptions<T, V> {
  onSuccess?: (data: T, vars: V) => unknown;
  onError?: (err: Error, vars: V) => void;
  /** Cache keys (or prefixes) to invalidate on success */
  invalidates?: string[];
}

export interface MutationResult<T, V> {
  mutate: (vars: V) => Promise<T>;
  loading: ReturnType<typeof signal<boolean>>;
  error: ReturnType<typeof signal<Error | null>>;
  data: ReturnType<typeof signal<T | undefined>>;
  reset: () => void;
}

export function useMutation<T, V = void>(
  fn: (vars: V) => Promise<T>,
  opts: UseMutationOptions<T, V> = {},
): MutationResult<T, V> {
  const fnRef = useRef(fn);
  fnRef.current = fn;
  const optsRef = useRef(opts);
  optsRef.current = opts;

  return useMemo(() => {
    const loading = signal(false);
    const error = signal<Error | null>(null);
    const data = signal<T | undefined>(undefined);
    return {
      loading,
      error,
      data,
      reset() {
        loading.value = false;
        error.value = null;
        data.value = undefined;
      },
      mutate(vars: V): Promise<T> {
        const promesa = ejecutar(vars);
        // `ejecutar` avisa por `onError` y ademas relanza, y el throw hace
        // falta: ReglasPage revierte su estado optimista en un catch, y
        // LeadsListPage no debe limpiar la seleccion ni cerrar el detalle si
        // la accion fallo.
        //
        // Pero la mayoria de los callers llama y se va
        // (`onClick={() => x.mutate()}`), y esa promesa rechazada sin nadie
        // escuchando salia como unhandled rejection: ruido en Sentry por un
        // error que el usuario ya ve como toast, y un CI en rojo con todos los
        // tests en verde (#394).
        //
        // Adjuntar aqui un handler la marca como atendida sin dejar de
        // rechazar: quien la espera sigue recibiendo su excepcion.
        //
        // OJO: cubre esta promesa, no las que salgan de encadenarla.
        // `mutate(x).finally(cb)` devuelve otra, que rechaza igual y sin
        // dueno. Para encadenar, el `.catch(() => {})` va antes del enlace.
        promesa.catch(() => {});
        return promesa;
      },
    };

    async function ejecutar(vars: V): Promise<T> {
      loading.value = true;
      error.value = null;
      try {
        const result = await fnRef.current(vars);
        data.value = result;
        loading.value = false;
        if (optsRef.current.invalidates) optsRef.current.invalidates.forEach(invalidate);
        await optsRef.current.onSuccess?.(result, vars);
        return result;
      } catch (e) {
        const err = e instanceof Error ? e : new Error(String(e));
        error.value = err;
        loading.value = false;
        optsRef.current.onError?.(err, vars);
        throw err;
      }
    }
     
  }, []);
}
